/**
 * Panel displaying callback information for a task.
 */
import type { FC } from "react";

import type { TaskSnapshot } from "../../../types";

import { formatRelativeTime } from "../../../utils/time";
import { ChevronRightIcon } from "../../common/icons";
import { JsonViewer } from "../../common/JsonViewer";
import { MetricCard } from "../../common/MetricCard";
import { PanelSection } from "../../common/PanelSection";

interface CallbackInfoPanelProps {
  callback: NonNullable<TaskSnapshot["last_callback"]>;
}

export const CallbackInfoPanel: FC<CallbackInfoPanelProps> = ({ callback }) => (
  <PanelSection
    title="Callback Information"
    padding="compact"
    className="!border-gray-200 !bg-transparent !shadow-none dark:!border-white/10"
  >
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {callback.status && <MetricCard label="Status" value={callback.status} />}
      {callback.node_id && (
        <MetricCard
          label="Node"
          value={callback.node_id}
          className="font-mono"
        />
      )}
      {callback.timestamp && (
        <MetricCard
          label="Received"
          value={formatRelativeTime(callback.timestamp)}
        />
      )}
      {typeof callback.progress === "number" && (
        <MetricCard
          label="Progress"
          value={
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-none bg-gray-100 dark:bg-white/10">
                <div
                  className="bg-arti-primary h-full transition-all duration-500 ease-out"
                  style={{
                    width: `${Math.min(100, Math.max(0, callback.progress))}%`,
                  }}
                />
              </div>
              <span className="font-mono text-xs font-medium text-gray-700 dark:text-gray-300">
                {callback.progress}%
              </span>
            </div>
          }
        />
      )}
      {callback.error && (
        <div className="sm:col-span-2">
          <MetricCard label="Error" value={callback.error} variant="danger" />
        </div>
      )}
    </div>
    {callback.result !== undefined && callback.result !== null && (
      <details className="group mt-4">
        <summary className="text-arti-primary hover:text-arti-primary-dark dark:text-arti-secondary dark:hover:text-arti-accent cursor-pointer select-none text-xs font-medium transition">
          <span className="inline-flex items-center gap-1">
            <ChevronRightIcon
              size={12}
              className="h-3 w-3 transition-transform group-open:rotate-90"
            />
            View Callback Result
          </span>
        </summary>
        <div className="mt-3">
          <JsonViewer
            data={callback.result}
            maxHeight="max-h-96"
            className="text-xs"
            containerClassName="rounded-none border border-slate-300 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-950/50"
          />
        </div>
      </details>
    )}
  </PanelSection>
);
